// The agent debug page's vocabulary: the agent roster it tabs through, the
// display label and role hint for each, and the parsers its URL search params are
// validated with (same shape as `lib/explorer-access.ts` does for the Explorer).
//
// The ORDER and the KEYS mirror `recruiter/repo/agent_trace.DISPLAY_AGENTS` (the
// backend decides which raw `agent_label` rolls up into which brain, and `other`
// is its catch-all). A brain missing from this list simply has no tab, which is
// how PULSE, HUE, JUNO, PORTIA and HERALD each went unnoticed for a while after
// they shipped — if you add an agent to the trace, add it here in the same PR.
//
// Pure data with no imports, so it is safe to read from a test or a component.

export const DEBUG_AGENTS: readonly string[] = [
  "mira",
  "atlas",
  "mason",
  "iris",
  "sage",
  "lens",
  "stylo",
  "hue",
  "scout",
  "sterling",
  "juno",
  "portia",
  "herald",
  "pulse",
  "gauge",
  "attention",
  "match",
  "tagger",
  "other",
];

/** Display name for a trace agent. Falls back to the raw key upper-cased, so a
 *  brain the backend added ahead of this list still renders readably. */
export const AGENT_LABEL: Record<string, string> = {
  mira: "MIRA",
  atlas: "ATLAS",
  mason: "MASON",
  iris: "IRIS",
  sage: "SAGE",
  lens: "LENS",
  stylo: "STYLO",
  hue: "HUE",
  scout: "SCOUT",
  sterling: "STERLING",
  juno: "JUNO",
  portia: "PORTIA",
  herald: "HERALD",
  pulse: "PULSE",
  gauge: "GAUGE",
  attention: "ATTENTION",
  match: "MATCH",
  tagger: "TAGGER",
  other: "OTHER",
};

/** `AGENT_LABEL` with a safe fallback for an unmapped key. */
export function agentLabel(agent: string): string {
  return AGENT_LABEL[agent] ?? agent.toUpperCase();
}

/** One-line role hint, shown under an EMPTY tab so a reader knows what they would
 *  have seen had that agent fired, instead of an unexplained blank panel. */
export const AGENT_BLURB: Record<string, string> = {
  mira: "The voice — acks, asks one question, paraphrases ATLAS.",
  atlas: "The tool-using operator — research, identity, search, task graph.",
  mason: "The brief composer — designs the printable brief at end of turn.",
  iris: "The identity editor — dedups & updates 'About You'.",
  sage: "Deep client research — SEO, web, headless browser.",
  lens: "Vision image placement onto the brief blocks.",
  stylo: "Workspace re-skin — tone, accent, density, locale.",
  hue: "PDF art-director — designs the export CSS (on demand).",
  scout: "Talent scout — picks talents likely to respond at budget.",
  sterling: "Negotiator — opens threads, judges replies.",
  juno: "Offer judge — scores each talent's offer (fit, price, timeline, risk) against the brief.",
  portia:
    "Finalist-card writer — at reveal, writes each finalist's card copy (a reasoning paragraph + real skill chips) from the profile, offer, and negotiation.",
  herald: "Activity-log narrator — turns each concierge action into the client's live feed line.",
  pulse:
    "Buyer-intent router — one high/low read per turn; sends casual clients to plain search instead of the concierge.",
  gauge:
    "Budget/timeline estimator — web-searches market rates and proposes a figure when the client won't give one.",
  attention:
    "Per-turn triage — reads the client's latest message for wants-a-human, frustration, abuse, a sensitive industry, and talent-not-client, each with a quoted reason.",
  match:
    "The deterministic search lane's judgement — the fast lane's batch grader and relaxation ladder, plus the guest funnel's relevance and experience scorers.",
  tagger:
    "The tagging system — every tagger's model call, whichever surface it reads (recruiter/tagging).",
  other: "Uncategorized calls — anything genuinely nobody's.",
};

/** The tab a fresh visit opens on. ATLAS, because it is the tool-using operator:
 *  most "why did Mira do that" questions are answered by what ATLAS decided. */
export const DEFAULT_DEBUG_AGENT = "atlas";

/** Narrow an `?agent=` search param to a real tab. An unknown value falls back to
 *  the default rather than rendering a tab strip with nothing selected — a stale
 *  bookmark should still open the page. */
export function parseDebugAgent(raw: unknown): string {
  return typeof raw === "string" && DEBUG_AGENTS.includes(raw) ? raw : DEFAULT_DEBUG_AGENT;
}

/** Narrow an `?agents=` search param (the turns-list filter) to real agent keys.
 *
 *  Comma-separated, order-preserving, deduped, and UNKNOWN KEYS ARE DROPPED —
 *  the value is sent to the backend as a filter, so a typo'd name would silently
 *  narrow the list to nothing rather than being ignored. Returns `[]` for
 *  anything unusable, which reads as "all agents". */
export function parseDebugAgents(raw: unknown): string[] {
  if (typeof raw !== "string") return [];
  const picked = raw
    .split(",")
    .map((a) => a.trim().toLowerCase())
    .filter((a) => DEBUG_AGENTS.includes(a));
  return [...new Set(picked)];
}

/** Which half of the page you are reading: ONE turn across every agent
 *  ("scoped"), or ONE agent across every turn ("feed"). */
export type DevelopersMode = "scoped" | "feed";

/** Narrow a `?mode=` search param. Anything unrecognized reads as "scoped", which
 *  is the default view and the one the rest of the URL describes. */
export function parseDevelopersMode(raw: unknown): DevelopersMode {
  return raw === "feed" ? "feed" : "scoped";
}

/** The turn-id prefix the backend mints for an Agent-Rerunner replay — the same
 *  constant as `recruiter/concierge/rerun.py::RERUN_TURN_PREFIX`.
 *
 *  Derived from the ID rather than read off a wire field ON PURPOSE: the prefix
 *  IS the marker, chosen by the backend so a replay is tellable apart with no
 *  schema change. If that constant ever moves, this one moves with it. */
export const REPLAY_TURN_PREFIX = "turn_rerun_";

/** True for an Agent-Rerunner replay turn.
 *
 *  A replay runs the REAL SCOUT loop over the project's live brief and traces
 *  identically to a live search — same agent, same call count, same cost — but
 *  writes nothing back: no `search_runs` row, no `scout_searches` row, no
 *  outreach. Unbadged, it therefore reads in a project's turn list as a SECOND
 *  genuine search on the conversation, which is exactly how one got reported as
 *  SCOUT searching twice (a bulk "latest N" sweep swept the project in 90
 *  minutes after its one real search).
 *
 *  Anything that is not a matching string reads as LIVE, which is the safe
 *  default: labelling a real client search as a replay would hide it. */
export function isReplayTurn(turnId: unknown): boolean {
  return typeof turnId === "string" && turnId.startsWith(REPLAY_TURN_PREFIX);
}
